import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

/**
 * ConditionBuilderController - Manages dynamic condition tree building
 *
 * Inspired by Jira/Zapier automation UIs:
 * - Visual AND/OR grouping
 * - Nested condition groups
 * - Field-aware operator selection
 * - Real-time JSON serialization
 *
 * Supports node types:
 * - field: Standard field comparison (field + operator + value)
 * - field_changed: Transition check (field + changed/changed_to/changed_from);
 *   derived from a transition operator on a normal field row
 * - cross_field: Compare two fields (field_a + operator + field_b)
 * - item_attribute: Dynamic attribute check (attribute_key + operator + value)
 * - association_count: Association count check (association + operator + value)
 */

const NO_VALUE_OPERATORS = [
  "is_null",
  "is_not_null",
  "is_blank",
  "is_present",
  "is_true",
  "is_false",
  "changed",
]

// Transition operators — serialized as a `field_changed` node so the rule fires
// once on the transition (e.g. "status changed to presented") instead of on
// every later edit while already in that state. Kept in sync with
// ConditionBuilderComponent::TRANSITION_OPERATORS. See CONEK-5920.
const TRANSITION_OPERATORS = [
  ["changed", "Changed"],
  ["changed_to", "Changed to"],
  ["changed_from", "Changed from"],
]

const TRANSITION_OPERATOR_VALUES = TRANSITION_OPERATORS.map(([value]) => value)

const COMPARISON_OPERATORS = [
  ["equals", "Equals"],
  ["not_equals", "Not equals"],
  ["greater_than", "Greater than"],
  ["less_than", "Less than"],
  ["greater_than_or_equal", "Greater than or equal"],
  ["less_than_or_equal", "Less than or equal"],
]

const OPERATOR_TYPES = {
  string: [
    ["equals", "Equals"],
    ["not_equals", "Not equals"],
    ["contains", "Contains"],
    ["not_contains", "Does not contain"],
    ["starts_with", "Starts with"],
    ["ends_with", "Ends with"],
    ["is_blank", "Is blank"],
    ["is_present", "Is present"],
  ],
  integer: [
    ["equals", "Equals"],
    ["not_equals", "Not equals"],
    ["greater_than", "Greater than"],
    ["less_than", "Less than"],
    ["greater_than_or_equal", "Greater than or equal"],
    ["less_than_or_equal", "Less than or equal"],
    ["is_null", "Is null"],
    ["is_not_null", "Is not null"],
    ["is_blank", "Is blank"],
    ["is_present", "Is not blank"],
  ],
  decimal: [
    ["equals", "Equals"],
    ["not_equals", "Not equals"],
    ["greater_than", "Greater than"],
    ["less_than", "Less than"],
    ["greater_than_or_equal", "Greater than or equal"],
    ["less_than_or_equal", "Less than or equal"],
    ["is_null", "Is null"],
    ["is_not_null", "Is not null"],
    ["is_blank", "Is blank"],
    ["is_present", "Is not blank"],
  ],
  boolean: [
    ["is_true", "Is true"],
    ["is_false", "Is false"],
    ["is_null", "Is null"],
    ["is_not_null", "Is not null"],
    ["is_blank", "Is blank"],
    ["is_present", "Is not blank"],
  ],
  datetime: [
    ["equals", "Equals"],
    ["not_equals", "Not equals"],
    ["before", "Before"],
    ["after", "After"],
    ["is_null", "Is null"],
    ["is_not_null", "Is not null"],
    ["is_blank", "Is blank"],
    ["is_present", "Is not blank"],
  ],
  date: [
    ["equals", "Equals"],
    ["not_equals", "Not equals"],
    ["before", "Before"],
    ["after", "After"],
    ["is_null", "Is null"],
    ["is_not_null", "Is not null"],
    ["is_blank", "Is blank"],
    ["is_present", "Is not blank"],
  ],
  // Fields backed by a fixed option list (catalog associations exposed via
  // their `key`, e.g. status.key) — value is picked from a <select>, so only
  // equality/membership/presence operators make sense. Kept in sync with
  // OPERATORS[:select] in condition_builder_component.rb. See CONEK-5436.
  select: [
    ["equals", "Equals"],
    ["not_equals", "Does not equal"],
    ["in", "Is one of"],
    ["not_in", "Is not one of"],
    ["is_blank", "Is blank"],
    ["is_present", "Is not blank"],
  ],
}

export default class extends Controller {
  static targets = [
    "output",
    "root",
    "conditions",
    "emptyState",
    "conditionTemplate",
    "groupTemplate",
    "crossFieldTemplate",
    "itemAttributeTemplate",
    "associationCountTemplate",
    "valueInputTemplate",
    "errorMessage",
  ]
  static values = {
    fields: Object,
    value: Object,
    maxDepth: { type: Number, default: 3 },
    disabled: { type: Boolean, default: false },
    fieldsUrl: String,
    associations: { type: Array, default: [] },
    attributeKeys: { type: Array, default: [] },
    // field_changed transition operators are opt-in (automation rules only,
    // never catalog validation). See CONEK-5920.
    transitionOperators: { type: Boolean, default: false },
  }

  connect() {
    this.updateOutput()
    this.updateAddButtonState()
  }

  // ── Select interop (Ui::SelectComponent / Tom Select) ──────────────────
  //
  // The condition selects are now rendered as Ui::SelectComponent, so each
  // marker (`.condition-field`, …) is a wrapper <div> around a native <select>
  // that Tom Select enhances. Reads go through the native <select> (Tom Select
  // keeps it synced); writes go through the Tom Select instance. These helpers
  // keep the rest of the controller agnostic and preserve the previous
  // native-select behavior (incl. plain <input> value controls).

  // Native <select> for a marker element (the wrapper div, or the select itself).
  selectFor(el) {
    if (!el) return null
    return el.matches("select") ? el : el.querySelector("select")
  }

  // Value of a marked control within scope — handles wrapper<select>, bare
  // <select>, and plain <input> (the text/number value control).
  controlValue(scope, selector) {
    const el = scope.querySelector(selector)
    if (!el) return ""
    if (el.matches("input, select")) return el.value
    const inner = el.querySelector("select, input")
    return inner ? inner.value : ""
  }

  // Run cb once the select's Tom Select instance exists. Cloned <template>
  // selects initialize Tom Select in a microtask after insertion, so we poll a
  // few frames; if it never initializes, cb receives null and we fall back to
  // native <option> manipulation.
  withTomSelect(selectEl, cb, attempts = 30) {
    if (!selectEl) return
    if (selectEl.tomselect) return cb(selectEl.tomselect)
    if (attempts <= 0) return cb(null)
    requestAnimationFrame(() => this.withTomSelect(selectEl, cb, attempts - 1))
  }

  // Replace a select's options. `pairs` are [value, text]. Preserves the empty
  // placeholder option (its text is kept unless `placeholder` is given).
  setSelectOptions(selectEl, pairs, { selected = "", placeholder } = {}) {
    if (!selectEl) return
    this.withTomSelect(selectEl, (ts) => {
      if (ts) {
        let ph = placeholder
        if (ph === undefined) {
          const empty = ts.options[""]
          ph = empty ? empty.text : undefined
        }
        ts.clear(true)
        ts.clearOptions()
        if (ph !== undefined) ts.addOption({ value: "", text: ph })
        pairs.forEach(([value, text]) => ts.addOption({ value, text }))
        ts.refreshOptions(false)
        if (selected) ts.setValue(selected, true)
      } else {
        let ph = placeholder
        if (ph === undefined) {
          const empty = selectEl.querySelector('option[value=""]')
          ph = empty ? empty.textContent : undefined
        }
        selectEl.innerHTML = ""
        if (ph !== undefined) {
          const o = document.createElement("option")
          o.value = ""
          o.textContent = ph
          selectEl.appendChild(o)
        }
        pairs.forEach(([value, text]) => {
          const o = document.createElement("option")
          o.value = value
          o.textContent = text
          if (value === selected) o.selected = true
          selectEl.appendChild(o)
        })
      }
    })
  }

  // Build a Ui::SelectComponent-equivalent value control (<div class="condition-value">
  // > [data-controller=ui--select] > select). Stimulus enhances it on insert.
  buildValueSelectWrapper(options, disabled) {
    const wrapper = document.createElement("div")
    wrapper.className = "condition-value flex-1 min-w-0"

    const inner = document.createElement("div")
    inner.className = "w-full"
    inner.setAttribute("data-controller", "ui--select")
    inner.setAttribute(
      "data-ui--select-options-value",
      JSON.stringify({
        sortable: false,
        allowEmptyOption: true,
        searchable: false,
        placeholder: "Select value...",
        disabled: !!disabled,
      }),
    )

    const select = document.createElement("select")
    select.className = "w-full"
    select.setAttribute("data-ui--select-target", "select")
    select.setAttribute(
      "data-action",
      "change->ui--automation--condition-builder#valueChanged",
    )
    select.setAttribute("aria-label", "Condition value")
    if (disabled) select.setAttribute("disabled", "")

    const ph = document.createElement("option")
    ph.value = ""
    ph.textContent = "Select value..."
    select.appendChild(ph)

    options.forEach(([label, value]) => {
      const o = document.createElement("option")
      o.value = value
      o.textContent = label
      select.appendChild(o)
    })

    inner.appendChild(select)
    wrapper.appendChild(inner)
    return wrapper
  }

  async loadFieldsForModel(modelName) {
    if (!this.hasFieldsUrlValue || !modelName) {
      this.showError("Cannot load fields: missing configuration")
      return
    }

    this.setLoadingState(true)

    try {
      this.clearError()
      const url = `${this.fieldsUrlValue}?model=${encodeURIComponent(modelName)}`
      const response = await get(url, {
        credentials: "include",
        responseKind: "json",
      })

      if (!response.ok) {
        this.showError(
          `Failed to load fields for model "${modelName}". Please try again.`,
        )
        return
      }

      const data = await response.json
      this.fieldsValue = this.convertFieldsToObject(data.fields || [])
      if (data.associations) this.associationsValue = data.associations
      if (data.attribute_keys) this.attributeKeysValue = data.attribute_keys
      this.updateAllFieldSelects()
      this.updateAddButtonState()
    } catch (error) {
      console.error("Error loading fields:", error)
      this.showError(
        "Network error: Could not load fields. Please check your connection and try again.",
      )
    } finally {
      this.setLoadingState(false)
    }
  }

  convertFieldsToObject(fieldsArray) {
    const fieldsObj = {}
    // Flatten recursively so association fields (e.g. status.key, delivered via
    // field.association.fields) are addressable by their dotted name — otherwise
    // a template condition on such a field can't pre-select and renders blank.
    // See CONEK-5436.
    const walk = (fields) => {
      fields.forEach((field) => {
        // selectable === false marks a container-only field (e.g. a catalog FK
        // superseded by its readable .key select) — recurse into its children
        // but don't offer it as a selectable field. See CONEK-5436.
        if (field.selectable !== false) {
          fieldsObj[field.name] = {
            type: field.type,
            label: field.label,
            operators: this.operatorsForField(field.name, field.type),
            options: field.options || [],
          }
        }
        if (field.association?.fields) walk(field.association.fields)
      })
    }
    walk(fieldsArray)
    return fieldsObj
  }

  getOperatorsForType(type) {
    return OPERATOR_TYPES[type] || OPERATOR_TYPES.string
  }

  // Operators for a specific field. Appends the transition operators when opted
  // in, but omits changed_from for dotted (association) fields — the evaluator
  // can never satisfy it there, so offering it would be silent dead config.
  // Kept in sync with ConditionBuilderComponent#operators_for_field. CONEK-5920.
  operatorsForField(name, type) {
    const base = this.getOperatorsForType(type)
    if (!this.transitionOperatorsValue) return base
    const transition = name.includes(".")
      ? TRANSITION_OPERATORS.filter(([value]) => value !== "changed_from")
      : TRANSITION_OPERATORS
    return base.concat(transition)
  }

  updateAllFieldSelects() {
    this.element.querySelectorAll(".condition-field").forEach((wrapper) => {
      const select = this.selectFor(wrapper)
      if (!select) return
      const currentValue = select.value
      const pairs = Object.keys(this.fieldsValue).map((fieldName) => [
        fieldName,
        this.fieldsValue[fieldName].label || fieldName,
      ])
      this.setSelectOptions(select, pairs, { selected: currentValue })
    })
  }

  // ── Add condition actions ──────────────────────────────────────────────

  addCondition(event) {
    if (this.disabledValue) return

    const container = this.findConditionsContainer(event.target)
    const template = this.conditionTemplateTarget.content.cloneNode(true)
    const row = template.querySelector(".condition-row")

    const index = container.querySelectorAll(
      ".condition-row, .condition-group.nested",
    ).length
    row.dataset.conditionIndex = index

    container.appendChild(row)
    this.hideEmptyState()
    this.updateAllFieldSelects()
    this.updateOutput()
  }

  addCrossField(event) {
    if (this.disabledValue) return
    if (!this.hasCrossFieldTemplateTarget) return

    const container = this.findConditionsContainer(event.target)
    const template = this.crossFieldTemplateTarget.content.cloneNode(true)
    const row = template.querySelector(".condition-row")

    const index = container.querySelectorAll(
      ".condition-row, .condition-group.nested",
    ).length
    row.dataset.conditionIndex = index

    container.appendChild(row)
    this.hideEmptyState()
    // Populate both field selects
    row.querySelectorAll(".condition-field").forEach((select) => {
      this.populateFieldSelect(select)
    })
    this.updateOutput()
  }

  addItemAttribute(event) {
    if (this.disabledValue) return
    if (!this.hasItemAttributeTemplateTarget) return

    const container = this.findConditionsContainer(event.target)
    const template = this.itemAttributeTemplateTarget.content.cloneNode(true)
    const row = template.querySelector(".condition-row")

    const index = container.querySelectorAll(
      ".condition-row, .condition-group.nested",
    ).length
    row.dataset.conditionIndex = index

    container.appendChild(row)
    this.hideEmptyState()
    // Populate attribute key select if we have keys
    const keySelect = this.selectFor(
      row.querySelector(".condition-attribute-key"),
    )
    if (keySelect && this.attributeKeysValue.length > 0) {
      const pairs = this.attributeKeysValue.map((key) => [
        key.name || key,
        key.label || key.name || key,
      ])
      this.setSelectOptions(keySelect, pairs, {})
    }
    this.updateOutput()
  }

  addAssociationCount(event) {
    if (this.disabledValue) return
    if (!this.hasAssociationCountTemplateTarget) return

    const container = this.findConditionsContainer(event.target)
    const template = this.associationCountTemplateTarget.content.cloneNode(true)
    const row = template.querySelector(".condition-row")

    const index = container.querySelectorAll(
      ".condition-row, .condition-group.nested",
    ).length
    row.dataset.conditionIndex = index

    container.appendChild(row)
    this.hideEmptyState()
    // Populate association select
    const assocSelect = this.selectFor(
      row.querySelector(".condition-association"),
    )
    if (assocSelect && this.associationsValue.length > 0) {
      const pairs = this.associationsValue.map((assoc) => [
        assoc.name || assoc,
        assoc.label || assoc.name || assoc,
      ])
      this.setSelectOptions(assocSelect, pairs, {})
    }
    this.updateOutput()
  }

  addGroup(event) {
    if (this.disabledValue) return

    const container = this.findConditionsContainer(event.target)
    const currentDepth = this.getCurrentDepth(container)

    if (currentDepth >= this.maxDepthValue - 1) {
      this.showError("Maximum nesting depth reached")
      return
    }

    const template = this.groupTemplateTarget.content.cloneNode(true)
    const group = template.querySelector(".condition-group")
    group.dataset.depth = currentDepth + 1

    const index = container.querySelectorAll(
      ".condition-row, .condition-group.nested",
    ).length
    group.dataset.conditionIndex = index

    container.appendChild(group)
    this.hideEmptyState()
    this.updateOutput()
  }

  removeCondition(event) {
    if (this.disabledValue) return

    const row = event.target.closest(".condition-row, .condition-group.nested")
    if (row) {
      row.remove()
      this.checkEmptyState()
      this.updateOutput()
    }
  }

  removeGroup(event) {
    this.removeCondition(event)
  }

  // ── Field change handlers ──────────────────────────────────────────────

  fieldChanged(event) {
    const row = event.target.closest(".condition-row")
    const nodeType = row?.dataset.nodeType

    // For cross_field rows, no operator update needed on field change
    if (nodeType === "cross_field") {
      this.updateOutput()
      return
    }

    const fieldName = event.target.value
    const operatorSelect = this.selectFor(
      row.querySelector(".condition-operator"),
    )

    if (fieldName && this.fieldsValue[fieldName]) {
      const fieldMeta = this.fieldsValue[fieldName]
      // operators are [value, label] pairs — matches setSelectOptions [value, text]
      const operators = fieldMeta.operators || []
      this.setSelectOptions(operatorSelect, operators, {
        placeholder: "Select operator...",
      })
      this.updateValueInput(row, fieldMeta)
    } else {
      this.setSelectOptions(operatorSelect, [], {
        placeholder: "Select operator...",
      })
    }

    this.updateOutput()
  }

  operatorChanged(event) {
    const row = event.target.closest(".condition-row")
    const operator = event.target.value
    const valueEl = row.querySelector(".condition-value")

    if (valueEl) {
      if (NO_VALUE_OPERATORS.includes(operator)) {
        valueEl.style.display = "none"
        const inner = this.selectFor(valueEl)
        if (inner?.tomselect) inner.tomselect.clear(true)
        else if (valueEl.matches("input, select")) valueEl.value = ""
        else if (inner) inner.value = ""
      } else {
        valueEl.style.display = ""
      }
    }

    this.updateOutput()
  }

  valueChanged(_event) {
    this.updateOutput()
  }

  updateGroupType(_event) {
    this.updateOutput()
  }

  // ── Serialization ──────────────────────────────────────────────────────

  updateOutput() {
    const tree = this.serializeGroup(this.rootTarget)
    this.outputTarget.value = JSON.stringify(tree)

    this.element.dispatchEvent(
      new CustomEvent("condition-builder:change", {
        detail: { value: tree },
        bubbles: true,
      }),
    )
  }

  serializeGroup(groupEl) {
    const typeSelect = this.selectFor(
      groupEl.querySelector(
        ":scope > .flex .condition-group-type, :scope > div > .condition-group-type",
      ),
    )
    const type = typeSelect?.value || "and"

    const conditionsContainer = groupEl.querySelector(
      ":scope > .conditions-container, :scope > .nested-conditions",
    )
    const conditions = []

    if (conditionsContainer) {
      const children = conditionsContainer.querySelectorAll(
        ":scope > .condition-row, :scope > .condition-group.nested",
      )

      children.forEach((child) => {
        if (child.classList.contains("condition-group")) {
          conditions.push(this.serializeGroup(child))
        } else {
          const serialized = this.serializeConditionRow(child)
          if (serialized) conditions.push(serialized)
        }
      })
    }

    return { type, conditions }
  }

  serializeConditionRow(row) {
    const nodeType = row.dataset.nodeType || "field"

    switch (nodeType) {
      case "cross_field": {
        const fieldA = this.controlValue(row, ".condition-field-a")
        const fieldB = this.controlValue(row, ".condition-field-b")
        const operator = this.controlValue(row, ".condition-operator")
        if (fieldA && fieldB && operator) {
          return {
            type: "cross_field",
            field_a: fieldA,
            operator,
            field_b: fieldB,
          }
        }
        return null
      }
      case "item_attribute": {
        const key = this.controlValue(row, ".condition-attribute-key")
        const operator = this.controlValue(row, ".condition-operator")
        const value = this.controlValue(row, ".condition-value")
        if (key && operator) {
          return {
            type: "item_attribute",
            attribute_key: key,
            operator,
            value: value || null,
          }
        }
        return null
      }
      case "association_count": {
        const assoc = this.controlValue(row, ".condition-association")
        const operator = this.controlValue(row, ".condition-operator")
        const value = this.controlValue(row, ".condition-value")
        if (assoc && operator) {
          return {
            type: "association_count",
            association: assoc,
            operator,
            value: value || null,
          }
        }
        return null
      }
      default: {
        const field = this.controlValue(row, ".condition-field")
        const operator = this.controlValue(row, ".condition-operator")
        const value = this.controlValue(row, ".condition-value")
        if (field && operator) {
          // A transition operator produces a field_changed node; the type is
          // derived from the operator, so the same field row round-trips both
          // current-value and transition conditions. See CONEK-5920.
          const type = TRANSITION_OPERATOR_VALUES.includes(operator)
            ? "field_changed"
            : "field"
          return { type, field, operator, value: value || null }
        }
        return null
      }
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  populateFieldSelect(wrapper) {
    const select = this.selectFor(wrapper)
    if (!select) return
    const pairs = Object.keys(this.fieldsValue).map((fieldName) => [
      fieldName,
      this.fieldsValue[fieldName].label || fieldName,
    ])
    this.setSelectOptions(select, pairs, { selected: select.value })
  }

  updateValueInput(row, fieldMeta) {
    const current = row.querySelector(".condition-value")
    if (!current) return

    if (fieldMeta.options && fieldMeta.options.length > 0) {
      // Field has fixed options → value control is a select.
      const select = this.selectFor(current)
      if (select) {
        // Already a select — just refresh its options (fieldMeta.options are [label, value]).
        this.setSelectOptions(
          select,
          fieldMeta.options.map(([label, value]) => [value, label]),
          { placeholder: "Select value..." },
        )
      } else {
        // Currently a plain input → swap for a Tom Select value control.
        current.replaceWith(
          this.buildValueSelectWrapper(fieldMeta.options, this.disabledValue),
        )
      }
    } else if (
      (this.selectFor(current) || current.matches("select")) &&
      this.hasValueInputTemplateTarget
    ) {
      // Field has no options but the control is still a select → swap for the
      // Ui::InputComponent value input (cloned from its template, DRY).
      const clone = this.valueInputTemplateTarget.content.cloneNode(true)
      const wrapper = clone.querySelector(".condition-value")
      const input = wrapper?.querySelector("input")
      if (input) {
        if (fieldMeta.type === "number") input.type = "number"
        if (this.disabledValue) input.disabled = true
      }
      current.replaceWith(wrapper)
    }
  }

  findConditionsContainer(target) {
    const group = target.closest(".condition-group")
    return (
      group?.querySelector(".conditions-container, .nested-conditions") ||
      this.conditionsTarget
    )
  }

  getCurrentDepth(container) {
    const group = container.closest(".condition-group")
    return parseInt(group?.dataset.depth || "0", 10)
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
  }

  checkEmptyState() {
    if (this.hasEmptyStateTarget && this.hasConditionsTarget) {
      const hasConditions =
        this.conditionsTarget.querySelectorAll(
          ".condition-row, .condition-group.nested",
        ).length > 0
      this.emptyStateTarget.classList.toggle("hidden", hasConditions)
    }
  }

  updateAddButtonState() {
    const hasFields =
      this.fieldsValue && Object.keys(this.fieldsValue).length > 0
    const addButtons = this.element.querySelectorAll(
      'button[data-action*="addCondition"]',
    )

    addButtons.forEach((button) => {
      button.disabled = !hasFields || this.disabledValue
      button.setAttribute(
        "aria-disabled",
        (!hasFields || this.disabledValue).toString(),
      )

      if (button.disabled) {
        button.classList.add("opacity-50", "cursor-not-allowed")
        button.classList.remove("cursor-pointer")
        button.title = "Please select a model first to add conditions"
      } else {
        button.classList.remove("opacity-50", "cursor-not-allowed")
        button.classList.add("cursor-pointer")
        button.title = ""
      }
    })
  }

  setLoadingState(isLoading) {
    const addButtons = this.element.querySelectorAll(
      'button[data-action*="addCondition"]',
    )
    addButtons.forEach((button) => {
      button.setAttribute("aria-busy", isLoading.toString())
    })
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = ""
      this.errorMessageTarget.classList.add("hidden")
    }
  }
}

// Re-export for use in templates
export { COMPARISON_OPERATORS }
